export enum Environment {
  LOCAL = "LOCAL",
  DEV = "DEV",
  STAGING = "STAGING",
  PROD = "PROD",
}

interface Config {
  apiUrl: string;
  wsUrl: string;
  allowApiDelay: boolean;
  cognito: {
    debug: boolean;
    region: string;
    userPoolId: string;
    webClientId: string;
    authDomain: string;
    redirectUri: string;
    redirectSignIn: string;
    redirectSignOut: string;
  };
  environment: Environment;
  showDevUtils: boolean;
  // Toggles represent feature toggles we expect that we can toggle
  // during runtime to change the behaviour of the page.
  toggles: {
    showExampleImages?: boolean;
    showExampleAuthor?: boolean;
    createExamSyllabus?: boolean;
  };
}

const configs: Record<keyof typeof Environment, Config> = {
  [Environment.LOCAL]: {
    apiUrl: "http://localhost:8080",
    wsUrl: "ws://localhost:8080",
    allowApiDelay: true,
    cognito: {
      debug: false,
      region: "eu-west-1",
      userPoolId: "eu-west-1_m8grjF8yY",
      webClientId: "7on43egu2lvgieivfi7r4rtp6i",
      authDomain: "auth.dev.cardiolearn.io",
      redirectUri: "http://localhost:3000/cognito-callback",
      redirectSignIn: "http://localhost:3000",
      redirectSignOut: "http://localhost:3000",
    },
    environment: Environment.LOCAL,
    showDevUtils: true,
    toggles: {
      showExampleImages: true,
      showExampleAuthor: true,
    },
  },
  [Environment.DEV]: {
    apiUrl: "https://test.cardiolearn.io",
    wsUrl: "wss://test.cardiolearn.io",
    allowApiDelay: true,
    cognito: {
      debug: true,
      region: "eu-west-1",
      userPoolId: "eu-west-1_m8grjF8yY",
      webClientId: "7on43egu2lvgieivfi7r4rtp6i",
      authDomain: "auth.dev.cardiolearn.io",
      redirectUri: "https://test.cardiolearn.io/cognito-callback",
      redirectSignIn: "https://test.cardiolearn.io",
      redirectSignOut: "https://test.cardiolearn.io",
    },
    environment: Environment.DEV,
    showDevUtils: true,
    toggles: {
      showExampleImages: true,
      showExampleAuthor: true,
    },
  },
  [Environment.STAGING]: {
    apiUrl: "",
    wsUrl: "",
    allowApiDelay: false,
    cognito: {
      debug: false,
      region: "eu-west-1",
      userPoolId: "",
      webClientId: "",
      authDomain: "",
      redirectUri: "",
      redirectSignIn: "",
      redirectSignOut: "",
    },
    environment: Environment.STAGING,
    showDevUtils: false,
    toggles: {},
  },
  [Environment.PROD]: {
    apiUrl: "https://adapt.cardiolearn.io",
    wsUrl: "wss://adapt.cardiolearn.io",
    allowApiDelay: false,
    cognito: {
      debug: false,
      region: "eu-west-1",
      userPoolId: "eu-west-1_bljrlRHwm",
      webClientId: "b4ntne5oghr4kle4l2qrd17gt",
      authDomain: "auth.cardiolearn.io",
      redirectUri: "https://adapt.cardiolearn.io/cognito-callback",
      redirectSignIn: "https://adapt.cardiolearn.io",
      redirectSignOut: "https://adapt.cardiolearn.io",
    },
    environment: Environment.PROD,
    showDevUtils: false,
    toggles: {},
  },
};

export function getEnvironment(origin: string = window.origin): Environment {
  if (
    /^https:\/\/(www\.){0,1}test/.test(origin) ||
    /\b(\w*netlify.app\w*)\b/.test(origin)
  ) {
    return Environment.DEV;
  } else if (/^https:\/\/(www\.){0,1}staging/.test(origin)) {
    return Environment.STAGING;
  } else if (/^https:\/\//.test(origin)) {
    return Environment.PROD;
  } else if (/^https:\/\/(www\.){0,1}adapt/.test(origin)) {
    return Environment.PROD;
  }
  return Environment.LOCAL;
}

export function getConfig(origin: string = window.origin): Config {
  let base = configs[getEnvironment(origin)];
  if (__OVERRIDE_API_ENDPOINT__) {
    base = {
      ...base,
      apiUrl: __OVERRIDE_API_ENDPOINT__,
    };
  }
  return base;
}

export const config = getConfig();
